import { lazy, ReactElement, Suspense } from 'react'
import {
  createBrowserRouter,
  Outlet,
  redirect,
  RouterProvider,
} from 'react-router-dom'

import ConvexErrorBoundaryWrapper from './components/ConvexErrorBoundaryWrapper'
import { ClockSyncProvider } from './helpers/ClockSync'

// Lazy load pages - only load when needed
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'))
const Admin = lazy(() => import('./pages/Admin'))
const AdminLogin = lazy(() => import('./pages/AdminLogin'))
const Calendar = lazy(() => import('./pages/Calendar'))
const Error = lazy(() => import('./pages/Error'))
const Player = lazy(() => import('./pages/Player'))
const ReaderPasswordGate = lazy(
  () => import('./pages/reader/ReaderPasswordGate'),
)
const PresentationWithTeams = lazy(
  () => import('./pages/PresentationWithTeams'),
)
const PresentationWithoutTeams = lazy(
  () => import('./pages/PresentationWithoutTeams'),
)
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const SchedulingForm = lazy(() => import('./pages/SchedulingForm'))
const Stats = lazy(() => import('./pages/stats/Stats'))

// Loading fallback component
const PageLoader = () => <div className="bg-white">Loading...</div>

// Layout wrapper for pages that need fixed viewport (game/reader pages)
const FixedViewportLayout = () => (
  <div className="_fixed-viewport">
    <Outlet />
  </div>
)

const App = (): ReactElement => {
  const router = createBrowserRouter([
    {
      path: '/',
      element: <Outlet />,
      errorElement: (
        <Suspense fallback={<PageLoader />}>
          <Error />
        </Suspense>
      ),
      children: [
        {
          index: true,
          loader: () => redirect('/results'),
        },
        {
          path: 'schedule/:token',
          element: (
            <Suspense fallback={<PageLoader />}>
              <SchedulingForm />
            </Suspense>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'calendar/:urlToken',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Calendar />
            </Suspense>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'admin/login',
          element: (
            <Suspense fallback={<PageLoader />}>
              <AdminLogin />
            </Suspense>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'admin/accept-invite',
          element: (
            <Suspense fallback={<PageLoader />}>
              <AcceptInvite />
            </Suspense>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'admin/reset-password',
          element: (
            <Suspense fallback={<PageLoader />}>
              <ResetPassword />
            </Suspense>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'admin',
          element: (
            <ConvexErrorBoundaryWrapper>
              <Suspense fallback={<PageLoader />}>
                <Admin />
              </Suspense>
            </ConvexErrorBoundaryWrapper>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'admin/matches',
          element: (
            <ConvexErrorBoundaryWrapper>
              <Suspense fallback={<PageLoader />}>
                <Admin />
              </Suspense>
            </ConvexErrorBoundaryWrapper>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'admin/teams',
          element: (
            <ConvexErrorBoundaryWrapper>
              <Suspense fallback={<PageLoader />}>
                <Admin />
              </Suspense>
            </ConvexErrorBoundaryWrapper>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'admin/users',
          element: (
            <ConvexErrorBoundaryWrapper>
              <Suspense fallback={<PageLoader />}>
                <Admin />
              </Suspense>
            </ConvexErrorBoundaryWrapper>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'admin/timeslots',
          element: (
            <ConvexErrorBoundaryWrapper>
              <Suspense fallback={<PageLoader />}>
                <Admin />
              </Suspense>
            </ConvexErrorBoundaryWrapper>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'admin/scheduling',
          element: (
            <ConvexErrorBoundaryWrapper>
              <Suspense fallback={<PageLoader />}>
                <Admin />
              </Suspense>
            </ConvexErrorBoundaryWrapper>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'admin/roster',
          loader: () => redirect('/admin/teams'),
        },
        {
          path: 'admin/weekly',
          element: (
            <ConvexErrorBoundaryWrapper>
              <Suspense fallback={<PageLoader />}>
                <Admin />
              </Suspense>
            </ConvexErrorBoundaryWrapper>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'admin/system',
          element: (
            <ConvexErrorBoundaryWrapper>
              <Suspense fallback={<PageLoader />}>
                <Admin />
              </Suspense>
            </ConvexErrorBoundaryWrapper>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'admin/profile',
          element: (
            <ConvexErrorBoundaryWrapper>
              <Suspense fallback={<PageLoader />}>
                <Admin />
              </Suspense>
            </ConvexErrorBoundaryWrapper>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'admin/system/email-logs',
          element: (
            <ConvexErrorBoundaryWrapper>
              <Suspense fallback={<PageLoader />}>
                <Admin />
              </Suspense>
            </ConvexErrorBoundaryWrapper>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'admin/system/templates',
          element: (
            <ConvexErrorBoundaryWrapper>
              <Suspense fallback={<PageLoader />}>
                <Admin />
              </Suspense>
            </ConvexErrorBoundaryWrapper>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'admin/system/archive',
          element: (
            <ConvexErrorBoundaryWrapper>
              <Suspense fallback={<PageLoader />}>
                <Admin />
              </Suspense>
            </ConvexErrorBoundaryWrapper>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'admin/system/settings',
          element: (
            <ConvexErrorBoundaryWrapper>
              <Suspense fallback={<PageLoader />}>
                <Admin />
              </Suspense>
            </ConvexErrorBoundaryWrapper>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'admin/system/users',
          element: (
            <ConvexErrorBoundaryWrapper>
              <Suspense fallback={<PageLoader />}>
                <Admin />
              </Suspense>
            </ConvexErrorBoundaryWrapper>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'admin/system/maintenance',
          element: (
            <ConvexErrorBoundaryWrapper>
              <Suspense fallback={<PageLoader />}>
                <Admin />
              </Suspense>
            </ConvexErrorBoundaryWrapper>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'admin/help',
          element: (
            <ConvexErrorBoundaryWrapper>
              <Suspense fallback={<PageLoader />}>
                <Admin />
              </Suspense>
            </ConvexErrorBoundaryWrapper>
          ),
          errorElement: (
            <Suspense fallback={<PageLoader />}>
              <Error />
            </Suspense>
          ),
        },
        {
          path: 'game/:urlToken',
          loader: ({ params }) => redirect(`/match/${params.urlToken}`),
        },
        {
          element: <FixedViewportLayout />,
          children: [
            {
              path: 'match/:urlToken',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ClockSyncProvider>
                    <Player />
                  </ClockSyncProvider>
                </Suspense>
              ),
              loader: ({ params }) => ({
                urlToken: params.urlToken,
              }),
            },
            {
              path: 'reader/:urlToken',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ClockSyncProvider>
                    <ReaderPasswordGate />
                  </ClockSyncProvider>
                </Suspense>
              ),
              loader: ({ params }) => ({
                urlToken: params.urlToken,
              }),
            },
            {
              path: 'stage/:urlToken',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ClockSyncProvider>
                    <PresentationWithTeams />
                  </ClockSyncProvider>
                </Suspense>
              ),
              loader: ({ params }) => ({
                urlToken: params.urlToken,
              }),
            },
            {
              path: 'room/:urlToken',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ClockSyncProvider>
                    <PresentationWithoutTeams />
                  </ClockSyncProvider>
                </Suspense>
              ),
              loader: ({ params }) => ({
                urlToken: params.urlToken,
              }),
            },
          ],
        },
        {
          path: 'stats',
          loader: () => redirect('/results'),
        },
        {
          path: 'results',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Stats />
            </Suspense>
          ),
          loader: () => ({
            entityType: 'results',
            entityId: undefined,
          }),
        },
        {
          path: 'results/season/:seasonName',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Stats />
            </Suspense>
          ),
          loader: ({ params }) => ({
            entityType: 'results',
            entityId: undefined,
            seasonName: params.seasonName,
          }),
        },
        {
          path: 'results/:matchId',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Stats />
            </Suspense>
          ),
          loader: ({ params }) => ({
            entityType: 'results',
            entityId: params.matchId,
          }),
        },
        {
          path: 'players',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Stats />
            </Suspense>
          ),
          loader: () => ({
            entityType: 'players',
            entityId: undefined,
          }),
        },
        {
          path: 'players/season/:seasonName',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Stats />
            </Suspense>
          ),
          loader: ({ params }) => ({
            entityType: 'players',
            entityId: undefined,
            seasonName: params.seasonName,
          }),
        },
        {
          path: 'players/:playerId',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Stats />
            </Suspense>
          ),
          loader: ({ params }) => ({
            entityType: 'players',
            entityId: params.playerId,
          }),
        },
        {
          path: 'teams',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Stats />
            </Suspense>
          ),
          loader: () => ({
            entityType: 'teams',
            entityId: undefined,
          }),
        },
        {
          path: 'teams/season/:seasonName',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Stats />
            </Suspense>
          ),
          loader: ({ params }) => ({
            entityType: 'teams',
            entityId: undefined,
            seasonName: params.seasonName,
          }),
        },
        {
          path: 'teams/:teamId',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Stats />
            </Suspense>
          ),
          loader: ({ params }) => ({
            entityType: 'teams',
            entityId: params.teamId,
          }),
        },
      ],
    },
  ])

  return <RouterProvider router={router} />
}

export default App
